import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Col, message, Modal, Row, Select, Table, Upload} from "antd";
import {DownloadOutlined, UploadOutlined} from "@ant-design/icons";
import {useStore} from "../../hooks/useStore";
import {b64DecodeToUrl} from "../../utils/helpers";
import {CourierFilter} from "../_defaultComponents/FilterComponents";
import moment from "moment";

export const WalletCourierFinancialUploads = () => {
    const stores = useStore();

    // State for filters and fetched data
    const [filters, setFilters] = useState({courierId: null, type: null});
    const [types, setTypes] = useState([]);
    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);

    // State for upload modal
    const [visible, setVisible] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [templateUrl, setTemplateUrl] = useState("");
    const templateBlobRef = useRef("");

    const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    useEffect(() => {
        let cancelled = false;
        const loadTemplate = async () => {
            try {
                const resp = await stores.walletStore.getCourierFinancialUploadsTemplate();
                if (cancelled) {
                    return;
                }
                if (resp.success && resp.data) {
                    if (templateBlobRef.current) {
                        window.URL.revokeObjectURL(templateBlobRef.current);
                    }
                    const url = await b64DecodeToUrl(resp.data, XLSX_MIME);
                    templateBlobRef.current = url;
                    setTemplateUrl(url);
                } else {
                    message.error("Şablon linki alınamadı.");
                }
            } catch (e) {
                console.error(e);
                message.error("Şablon linki yüklenirken hata oluştu.");
            }
        };
        loadTemplate();
        return () => {
            cancelled = true;
            if (templateBlobRef.current && templateBlobRef.current.startsWith("blob:")) {
                window.URL.revokeObjectURL(templateBlobRef.current);
                templateBlobRef.current = "";
            }
        };
    }, []);

    const handleTemplateDownload = useCallback(() => {
        if (!templateUrl) {
            return;
        }
        const link = document.createElement("a");
        link.href = templateUrl;
        link.download = "Kesinti_Ekleme_Sablonu.xlsx";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [templateUrl]);

    // Load available types on mount
    useEffect(() => {
        const loadTypes = async () => {
            try {
                const resp = await stores.walletStore.listUploadTypes();
                if (resp.success && Array.isArray(resp.data)) {
                    setTypes(resp.data);
                } else {
                    message.error("Tipleri yüklerken hata oluştu.");
                }
            } catch (e) {
                message.error("Tipleri yüklerken hata oluştu.");
            }
        };
        loadTypes();
    }, []);

    const fetchData = async () => {
        if (!filters.courierId) return; // Sadece courierId seçiliyse veri çek

        setLoading(true);
        try {
            const response = await stores.walletStore.listWalletCourierFinancials({
                courierId: filters.courierId,
                type: filters.type || undefined // type undefined ise gönderme
            });
            if (response.success) {
                setData(response.data);
                setCount(response.count);
            } else {
                message.error(response.message || "Veri yüklenemedi");
            }
        } catch (err) {
            console.error(err);
            message.error("Sunucu hatası oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // Filtre değişikliklerinde otomatik veri çek
    useEffect(() => {
        fetchData();
    }, [filters.courierId, filters.type]);

    // Columns for the table
    const columns = [
        {title: "Eklenti Kesinti Tipi", dataIndex: "adjustmentType", key: "adjustmentType"},
        {
            title: "Tutar",
            dataIndex: "totalAmount",
            key: "totalAmount",
            render: val => `${val} ₺`
        },
        {title: "Açıklama", dataIndex: "description", key: "description"},
        {
            title: "Yükleyen",
            dataIndex: "createdBy",
            key: "createdBy",
            render: val => val?.fullName || "-"
        },
        {
            title: "Yüklenme Tarihi",
            dataIndex: "createdAt",
            key: "createdAt",
            render: val => moment(val).format("DD.MM.YYYY HH:mm:ss")
        },
        {
            title: "İşlem Tarihi",
            dataIndex: "calculatedAt",
            key: "calculatedAt",
            render: val => moment(val).format("DD.MM.YYYY HH:mm:ss")
        }
    ];

    // Upload props
    const fileUploadProps = {
        name: "file",
        multiple: false,
        accept: ".xlsx,.xls,.csv",
        showUploadList: false,
        customRequest: async ({file, onSuccess, onError}) => {
            setImportLoading(true);
            try {
                const formData = new FormData();
                formData.append("file", file);
                const response = await stores.walletStore.uploadWalletCourierFinancialFile(
                    formData
                );
                const errorExcel = response?.data?.errorExcel ?? response?.data?.errorReport;
                if (errorExcel) {
                    const url = await b64DecodeToUrl(errorExcel);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `Hata_Raporu_${file.name}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => window.URL.revokeObjectURL(url), 200);
                }

                if (response?.data?.success === true) {
                    stores.appStore.openSuccessModal({
                        title: "Kesinti/Ekleme dosyası başarıyla yüklendi!",
                        content:
                            response.data.missingCouriers?.length > 0
                                ? `Bazı kuryeler bulunamadı: ${response.data.missingCouriers.join(
                                      ", "
                                  )}`
                                : response.data.invalidCount > 0
                                ? `${response.data.invalidCount} adet hatalı satır bulundu. Hata raporu otomatik olarak indirildi.`
                                : undefined
                    });
                    // Refresh data with current filters
                    if (filters.courierId) {
                        fetchData();
                    }
                } else if (response?.data?.success === false) {
                    const invalidCount = response.data.invalidCount;
                    stores.appStore.openErrorModal({
                        title: "Yükleme sırasında satır hataları bulundu",
                        content:
                            invalidCount != null
                                ? `${invalidCount} adet hatalı satır bulundu. Hata raporu otomatik olarak indirildi.`
                                : "Dosyada doğrulama hataları var. Hata raporu otomatik olarak indirildi."
                    });
                } else {
                    message.error(response?.data?.message || "Dosya yüklenirken bir hata oluştu.");
                }
                onSuccess(null, file);
                setVisible(false);
            } catch (err) {
                console.error(err);
                onError(err);
                message.error(`${file.name} yüklenirken hata oluştu.`);
            } finally {
                setImportLoading(false);
            }
        }
    };

    return (
        <>
            {/* Filters */}
            <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                    <CourierFilter
                        value={filters.courierId}
                        onChange={val => setFilters({...filters, courierId: val})}
                        allowClear
                    />
                </Col>
                <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                    <Select
                        placeholder="Tip seçin"
                        value={filters.type}
                        onChange={val => setFilters({...filters, type: val})}
                        style={{minWidth: 160, width: "100%"}}
                        allowClear
                    >
                        {types?.map(t => (
                            <Select.Option key={t._id} value={t._id}>
                                {t.name}
                                {t.type === "deduction"
                                    ? " (Kesinti)"
                                    : t.type === "addition"
                                    ? " (Eklenti)"
                                    : ""}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>
                <Col
                    xs={24}
                    sm={24}
                    md={12}
                    lg={12}
                    xl={12}
                    style={{display: "flex", gap: 8, justifyContent: "flex-end"}}
                >
                    <Button
                        type="default"
                        icon={<DownloadOutlined />}
                        onClick={handleTemplateDownload}
                        disabled={!templateUrl}
                    >
                        Şablon İndir
                    </Button>
                    <Button type="primary" onClick={() => setVisible(true)}>
                        Kesinti/Ekleme Yükle
                    </Button>
                </Col>
            </Row>

            {/* Data Table */}
            <div style={{marginTop: 16}}>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="_id"
                    loading={loading}
                    scroll={{x: 1000}}
                    pagination={{pageSize: 20, total: count}}
                />
            </div>

            {/* Upload Modal */}
            <Modal
                title="Kesinti/Ekleme Yükle"
                visible={visible}
                confirmLoading={importLoading}
                onCancel={() => setVisible(false)}
                footer={null}
            >
                <Upload {...fileUploadProps}>
                    <Button icon={<UploadOutlined />}>Dosya Seç</Button>
                </Upload>
            </Modal>
        </>
    );
};
